import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import { experienceBlockDraftSchema } from '@matter_extractor/shared';
import { z } from 'zod';

/**
 * Un bloc dans le PRESSE-PAPIER DU SYSTÈME (refonte modulaire §9.3 ; grill du
 * 2026-09-23 ; issue #136).
 *
 * « Je veux pouvoir copier-coller un bloc d'une page à une autre. » Le presse-
 * papier du système plutôt qu'un état de l'écran, et ce n'est pas un détail de
 * mise en œuvre : il survit à un rechargement, à un aller-retour par la
 * Timeline, et il traverse les ONGLETS - copier un bloc de l'expérience A et le
 * coller dans B, ce qu'aucun `useState` ne peut offrir.
 *
 * ## Ce qui en sort est une ENTRÉE NON FIABLE
 *
 * N'importe quoi peut se trouver dans un presse-papier : le texte d'un courriel,
 * un JSON d'une autre application, une charge forgée. C'est exactement une
 * frontière au sens du `CLAUDE.md` racine - « tout ce qui traverse une frontière
 * de confiance est validé par Zod » - et c'est pourquoi `blockOfClipboard`
 * parse au lieu de caster. Un presse-papier qui ne porte pas un bloc rend
 * `null`, et le collage ne fait rien : ni erreur, ni bloc à moitié construit.
 *
 * ## L'enveloppe, et pourquoi elle est nommée
 *
 * Le JSON est enveloppé dans `{ kind, version, block }` plutôt que sérialisé
 * nu. Nu, un objet `{ type: 'text', ... }` venu d'ailleurs pourrait passer le
 * schéma par accident ; nommé, il faut l'avoir voulu. Et `version` donne un
 * endroit où refuser proprement le jour où la forme d'un bloc changera - un
 * presse-papier peut être VIEUX, il traverse les rechargements.
 *
 * Le bloc est stocké **tel quel**, identifiant compris. Le réadressage - un
 * identifiant neuf, un nom de variable suffixé contre la timeline d'arrivée -
 * appartient au collage et pas à la copie : c'est la destination qui sait quels
 * noms sont déjà pris, et `experience-duplicate.ts` le fait déjà pour la
 * duplication (§4.2).
 */

/** Ce que ce dépôt reconnaît comme sien dans un presse-papier. */
const CLIPBOARD_KIND = 'matter-extractor/block';

/** La forme d'aujourd'hui. Un presse-papier peut être vieux - voir la tête. */
const CLIPBOARD_VERSION = 1;

const clipboardBlockSchema = z.object({
  kind: z.literal(CLIPBOARD_KIND),
  version: z.literal(CLIPBOARD_VERSION),
  block: experienceBlockDraftSchema,
});

/** Le bloc, tel qu'il part dans le presse-papier. */
export function clipboardOfBlock(block: ExperienceBlockDraft): string {
  return JSON.stringify({ kind: CLIPBOARD_KIND, version: CLIPBOARD_VERSION, block });
}

/**
 * Le bloc qu'un presse-papier porte, ou `null` - voir la tête.
 *
 * `JSON.parse` est enveloppé parce qu'il JETTE sur une chaîne quelconque, et
 * qu'un presse-papier en porte une la plupart du temps : la forme normale de
 * cette fonction est de rendre `null` sans que rien n'ait mal tourné.
 */
export function blockOfClipboard(text: string): ExperienceBlockDraft | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  const parsed = clipboardBlockSchema.safeParse(raw);
  return parsed.success ? parsed.data.block : null;
}
